import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import AppLayout from "../layout/AppLayout";
import QuickActionCard from "../components/dashboard/QuickActionCard";
import StatCard from "../components/dashboard/StatCard";
import StatusBadge from "../components/ui/StatusBadge";
import { getApplicationDrafts } from "../services/applicationService";
import { getDesignApplicationDrafts } from "../services/designApplicationService";
import { getPatentApplicationDrafts } from "../services/patentApplicationService";
import {
  FiArrowRight,
  FiFileText,
  FiMessageSquare,
  FiPenTool,
  FiPlus,
  FiSearch,
  FiShield,
  FiTag,
  FiX,
} from "react-icons/fi";
import { initialsOf } from "../utils/initials";
import { PATENT_PROCEDURE_LABELS, getPatentProcedureLabel } from "../utils/patentProcedure";

interface DashboardApplication {
  _id: string;
  kind: "trademark" | "design" | "patent";
  procedureType?: "EP1001" | "EP1200";
  referenceNumber: string;
  ownerId: string;
  ownerName: string;
  status: string;
  updatedAt: Date;
}

type RecentKindFilter = "all" | DashboardApplication["kind"];
type RecentLimit = "5" | "10" | "20" | "30" | "all";

const KIND_TABS: Array<{ value: RecentKindFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "trademark", label: "Trademarks" },
  { value: "design", label: "Designs" },
  { value: "patent", label: "Patents" },
];

const selectClass =
  "h-10 rounded-[10px] border border-appLine bg-white px-3 text-[13px] text-appInk focus:border-dantzOrange focus:outline-none focus:ring-1 focus:ring-dantzOrange";

function formatPersonName(person: any) {
  if (!person) return "";

  const fullName = [person.firstName, person.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || person.email || "";
}

function getOwnerId(app: any) {
  return typeof app.userId === "string" ? app.userId : app.userId?._id || "";
}

function getOwnerName(app: any, currentUser: any) {
  const populatedName = formatPersonName(app.userId);
  if (populatedName) return populatedName;

  const ownerId = getOwnerId(app);
  if (currentUser && ownerId === currentUser.id) {
    return formatPersonName(currentUser);
  }

  return app.userId?.email || "Unknown user";
}

function mapApplication(kind: DashboardApplication["kind"], currentUser: any) {
  return (app: any): DashboardApplication => ({
    _id: app._id,
    kind,
    procedureType: app.procedureType,
    referenceNumber: app.referenceNumber?.referenceNumber || "",
    ownerId: getOwnerId(app),
    ownerName: getOwnerName(app, currentUser),
    status: app.status,
    updatedAt: new Date(app.updatedAt),
  });
}

function applicationPath(app: DashboardApplication) {
  if (app.kind === "patent" && app.procedureType === "EP1200") {
    const action =
      app.status === "PAID" ||
      app.status === "PAYMENT_PENDING" ||
      app.status === "AWAITING_RELEASE"
        ? "view"
        : "edit";
    return `/patent-applications/${app._id}/ep1200/${action}`;
  }

  const base =
    app.kind === "trademark"
      ? "/applications"
      : app.kind === "design"
      ? "/design-applications"
      : "/patent-applications";
  const action =
    app.status === "PAID" ||
    app.status === "PAYMENT_PENDING" ||
    app.status === "AWAITING_RELEASE"
      ? "view"
      : "edit";
  return `${base}/${app._id}/${action}`;
}

function kindLabel(app: DashboardApplication) {
  return app.kind === "trademark"
    ? "Trademark"
    : app.kind === "design"
    ? "Design"
    : `Patent · ${getPatentProcedureLabel(app.procedureType)}`;
}

export default function HomePage() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const user = useSelector((state: RootState) => state.auth.user);
  const userId = user?.id;
  const userFirstName = user?.firstName;
  const userLastName = user?.lastName;
  const userEmail = user?.email;
  const userIsAdmin = user?.isAdmin;
  const userSnapshot = useMemo(
    () => userId
      ? {
          id: userId,
          firstName: userFirstName,
          lastName: userLastName,
          email: userEmail,
          isAdmin: userIsAdmin,
        }
      : null,
    [userId, userFirstName, userLastName, userEmail, userIsAdmin],
  );
  const [applications, setApplications] = useState<DashboardApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPatentTypeDialog, setShowPatentTypeDialog] = useState(false);
  const [selectedPatentProcedure, setSelectedPatentProcedure] = useState<"EP1001" | "EP1200">("EP1001");
  const [recentLimit, setRecentLimit] = useState<RecentLimit>("5");
  const [recentKindFilter, setRecentKindFilter] = useState<RecentKindFilter>("all");
  const [recentOwnerFilter, setRecentOwnerFilter] = useState("all");
  const [search, setSearch] = useState("");

  // "New application" on other pages comes here as /home#new
  useEffect(() => {
    if (hash === "#new") {
      document.getElementById("new")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  useEffect(() => {
    async function load() {
      try {
        const [trademarkData, designData, patentData] = await Promise.all([
          getApplicationDrafts(),
          getDesignApplicationDrafts(),
          getPatentApplicationDrafts(),
        ]);

        setApplications(
          [
            ...trademarkData.map(mapApplication("trademark", userSnapshot)),
            ...designData.map(mapApplication("design", userSnapshot)),
            ...patentData.map(mapApplication("patent", userSnapshot)),
          ].sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()),
        );
      } catch {
        // dashboard stays usable without data — sections show their empty states
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [userSnapshot]);

  const stats = useMemo(
    () => ({
      total: applications.length,
      drafts: applications.filter((app) => app.status === "DRAFT").length,
      submitted: applications.filter((app) =>
        ["SUBMITTED", "AWAITING_RELEASE", "PAYMENT_PENDING", "PAID"].includes(app.status),
      ).length,
      paid: applications.filter((app) => app.status === "PAID").length,
    }),
    [applications],
  );

  const recentOwners = useMemo(() => {
    const owners = new Map<string, string>();

    applications.forEach((app) => {
      if (app.ownerId) {
        owners.set(app.ownerId, app.ownerName);
      }
    });

    return Array.from(owners.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [applications]);

  const recentFilteredApplications = useMemo(() => {
    const term = search.trim().toLowerCase();

    return applications.filter((app) => {
      const matchesKind = recentKindFilter === "all" || app.kind === recentKindFilter;
      const matchesOwner = recentOwnerFilter === "all" || app.ownerId === recentOwnerFilter;
      const matchesSearch =
        !term ||
        app.referenceNumber.toLowerCase().includes(term) ||
        app.ownerName.toLowerCase().includes(term);
      return matchesKind && matchesOwner && matchesSearch;
    });
  }, [applications, recentKindFilter, recentOwnerFilter, search]);

  const recent = useMemo(() => {
    if (recentLimit === "all") return recentFilteredApplications.slice(0, 100);

    return recentFilteredApplications.slice(0, Number(recentLimit));
  }, [recentFilteredApplications, recentLimit]);

  const closePatentTypeDialog = () => {
    setSelectedPatentProcedure("EP1001");
    setShowPatentTypeDialog(false);
  };

  const startPatentApplication = () => {
    setShowPatentTypeDialog(false);
    setSelectedPatentProcedure("EP1001");
    navigate(
      selectedPatentProcedure === "EP1200"
        ? "/patent-applications/ep1200/new"
        : "/patent-applications/new?procedureType=EP1001",
    );
  };

  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <AppLayout crumb="Dashboard" contentWidth="max-w-[1200px]">
      <div className="flex flex-col gap-8">
        <header className="flex flex-wrap items-end justify-between gap-5">
          <div className="flex flex-col gap-1.5">
            <p className="text-[13px] font-medium tracking-[0.02em] text-appMuted">{today}</p>
            <h1 className="font-heading text-[30px] font-semibold tracking-[-0.01em] text-dantzBlue">
              Welcome back{user ? `, ${user.firstName}` : ""}
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex h-11 w-[280px] max-w-full items-center gap-2.5 rounded-[10px] border border-[#DDE2E8] bg-white px-3.5 focus-within:border-dantzOrange focus-within:ring-1 focus-within:ring-dantzOrange">
              <FiSearch size={18} className="shrink-0 text-appMuted" aria-hidden="true" />
              <span className="sr-only">Search applications</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={user?.isAdmin ? "Search applications, owners…" : "Search applications…"}
                className="w-full border-none bg-transparent text-sm text-appInk outline-none"
              />
            </label>

            <button
              type="button"
              onClick={() =>
                document.getElementById("new")?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
              className="inline-flex h-11 items-center gap-2 rounded-[10px] bg-[#C2610C] px-[18px] text-sm font-semibold text-white transition hover:bg-[#A8530A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dantzOrange"
            >
              <FiPlus size={18} />
              New application
            </button>
          </div>
        </header>

        <section id="new" className="flex flex-col gap-3.5">
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-appMuted">
            Start something new
          </h2>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3.5">
            <QuickActionCard
              title="Register trademark"
              text="Word or figurative mark"
              icon={<FiTag size={20} />}
              onClick={() => navigate("/trademark/new")}
            />
            <QuickActionCard
              title="Register design"
              text="New design application"
              icon={<FiPenTool size={20} />}
              onClick={() => navigate("/designs/new")}
            />
            <QuickActionCard
              title="Register patent"
              text="European patent application"
              icon={<FiFileText size={20} />}
              onClick={() => setShowPatentTypeDialog(true)}
            />
            <QuickActionCard
              tone="secondary"
              title="File an opposition"
              text="Start a new opposition"
              icon={<FiShield size={20} />}
              onClick={() => navigate("/opposition/new")}
            />
            <QuickActionCard
              tone="secondary"
              title="Respond to office action"
              text="Reply to the IP office"
              icon={<FiMessageSquare size={20} />}
              onClick={() => navigate("/office-action/new")}
            />
          </div>
        </section>

        {showPatentTypeDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
            <div className="w-full max-w-lg rounded-xl border border-secondary5 bg-white p-6 shadow-xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-heading text-lg font-medium text-dantzBlue">
                    Select patent filing type
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={closePatentTypeDialog}
                  className="rounded-md p-1 text-secondary4 transition hover:bg-dantzGray hover:text-dantzBlue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dantzOrange"
                  aria-label="Close"
                >
                  <FiX size={20} />
                </button>
              </div>

              <label className="mt-5 block text-sm font-medium text-secondary3">
                Filing type
              </label>
              <select
                value={selectedPatentProcedure}
                onChange={(event) =>
                  setSelectedPatentProcedure(event.target.value as "EP1001" | "EP1200")
                }
                className="mt-2 w-full rounded-lg border border-secondary5 bg-white px-3 py-2.5 text-sm text-dantzBlue shadow-sm focus:border-dantzOrange focus:outline-none focus:ring-1 focus:ring-dantzOrange"
              >
                <option value="EP1001">
                  {PATENT_PROCEDURE_LABELS.EP1001}
                </option>
                <option value="EP1200">
                  {PATENT_PROCEDURE_LABELS.EP1200}
                </option>
              </select>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closePatentTypeDialog}
                  className="rounded-lg border border-secondary5 bg-white px-4 py-2 text-sm font-medium text-secondary3 transition hover:bg-dantzGray"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={startPatentApplication}
                  className="rounded-lg bg-dantzBlue px-4 py-2 text-sm font-medium text-white transition hover:bg-secondary1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dantzOrange"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        <section className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3.5">
          <StatCard solid label="All applications" value={stats.total} />
          <StatCard label="Drafts" value={stats.drafts} />
          <StatCard label="Submitted" value={stats.submitted} />
          <StatCard label="Paid" value={stats.paid} />
        </section>

        <section className="flex flex-col overflow-hidden rounded-2xl border border-appLine bg-white">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#EEF1F4] px-6 py-5">
            <h2 className="font-heading text-lg font-semibold text-dantzBlue">
              Recent applications
            </h2>

            <div className="flex flex-wrap items-center gap-3">
              <div
                role="group"
                aria-label="Filter by protection type"
                className="flex gap-1 rounded-[10px] bg-[#F1F3F6] p-1"
              >
                {KIND_TABS.map((tab) => {
                  const active = recentKindFilter === tab.value;

                  return (
                    <button
                      key={tab.value}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setRecentKindFilter(tab.value)}
                      className={`h-9 rounded-lg px-3.5 text-[13px] ${
                        active
                          ? "bg-white font-semibold text-dantzBlue shadow-[0_1px_2px_rgba(15,30,50,0.12)]"
                          : "font-medium text-[#4A5565] hover:text-dantzBlue"
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              <label className="flex items-center gap-2 text-[13px] text-[#4A5565]">
                Show
                <select
                  value={recentLimit}
                  onChange={(event) => setRecentLimit(event.target.value as RecentLimit)}
                  className={selectClass}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="30">30</option>
                  <option value="all">All</option>
                </select>
              </label>

              {user?.isAdmin && (
                <label className="flex items-center gap-2 text-[13px] text-[#4A5565]">
                  Owner
                  <select
                    value={recentOwnerFilter}
                    onChange={(event) => setRecentOwnerFilter(event.target.value)}
                    className={`${selectClass} max-w-56`}
                  >
                    <option value="all">All users</option>
                    {recentOwners.map((owner) => (
                      <option key={owner.id} value={owner.id}>
                        {owner.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <button
                onClick={() => navigate("/applications")}
                className="flex items-center gap-1 text-[13px] font-semibold text-dantzBlue hover:underline"
              >
                View all
                <FiArrowRight size={14} />
              </button>
            </div>
          </div>

          {loading && (
            <div className="space-y-3 px-6 py-5">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-10 animate-pulse rounded-lg bg-gray-100"
                />
              ))}
            </div>
          )}

          {!loading && recent.length === 0 && (
            <p className="px-6 py-5 text-sm text-appMuted">
              No applications yet. start your first one above.
            </p>
          )}

          {!loading && recent.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] border-collapse text-sm">
                <thead>
                  <tr className="bg-[#FAFBFC] text-left text-xs font-semibold uppercase tracking-[0.06em] text-appMuted">
                    <th className="px-6 py-3 font-semibold">Application</th>
                    {user?.isAdmin && <th className="px-4 py-3 font-semibold">Owner</th>}
                    <th className="px-4 py-3 font-semibold">Updated</th>
                    <th className="py-3 pl-4 pr-6 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((app) => (
                    <tr
                      key={`${app.kind}-${app._id}`}
                      onClick={() => navigate(applicationPath(app))}
                      className="cursor-pointer border-t border-[#EEF1F4] transition-colors hover:bg-appBg/70"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3.5">
                          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[9px] bg-appTile text-dantzBlue">
                            {app.kind === "trademark" ? (
                              <FiTag size={18} />
                            ) : app.kind === "design" ? (
                              <FiPenTool size={18} />
                            ) : (
                              <FiFileText size={18} />
                            )}
                          </span>
                          <div className="flex min-w-0 flex-col gap-[3px]">
                            <button
                              type="button"
                              className="w-fit max-w-full truncate text-left font-semibold text-appInk"
                            >
                              {app.referenceNumber || "No reference yet"}
                            </button>
                            <span className="text-[13px] text-appMuted">{kindLabel(app)}</span>
                          </div>
                        </div>
                      </td>
                      {user?.isAdmin && (
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2.5">
                            <span
                              aria-hidden="true"
                              className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-full bg-appAvatar text-xs font-semibold text-dantzBlue"
                            >
                              {initialsOf(app.ownerName)}
                            </span>
                            <span className="whitespace-nowrap text-[#253244]">{app.ownerName}</span>
                          </div>
                        </td>
                      )}
                      <td className="whitespace-nowrap px-4 py-4 text-[#4A5565]">
                        {app.updatedAt.toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td className="py-4 pl-4 pr-6">
                        <StatusBadge status={app.status} tinted />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AppLayout>
  );
}
